import { useCallback } from "react";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { downloadSettingExcel } from "src/utils/blob/blobUtil";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { downloadExcel_PRD1020_R01, getPRD1020_R01List } from "./Api_PRD1020_R01";
import { defaultPRD1020_R01Dto, PRD1020_R01Dto } from "./Dto_PRD1020_R01";
import { usePRD1020_R01Store } from "./Store_PRD1020_R01";

interface Props {
	/** 공통 Message 영역에 조회 완료 문구를 표시하기 위한 전역 UI setter */
	setMessage: (message: string) => void;
}

/**
 * PRD1020_R01의 사용자 동작을 처리하는 화면 로직 모듈이다.
 * 조회 응답을 Grid에서 사용할 수 있도록 행 식별 순서를 부여하고 Store에 저장하며,
 * 조회와 Excel 요청 동안 전역 로딩 스피너를 관리한다.
 */
export const Content_PRD1020_R01 = ({ setMessage }: Props) => {
	const {
		PRD1020_R01Filter,
		setPRD1020_R01PastFilter,
		setPRD1020_R01List,
		setScrollTop,
	} = usePRD1020_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	// 공통 Actionbar가 현재 화면 코드의 조회·Excel 권한을 사용할 수 있도록 등록한다.
	useFetchActionbarAuth("PRD1020_R01");

	/** 현재 검색조건으로 목록을 조회하여 Grid 표시용 목록과 직전 검색조건을 갱신한다. */
	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		// 새 조회 결과는 첫 위치에서 보여야 하므로 저장된 Grid 스크롤 위치를 제거한다.
		setScrollTop(null);
		try {
			const response = await getPRD1020_R01List(PRD1020_R01Filter);
			/*
			 * 서버 업무 필드는 그대로 유지하고 공통 Grid 식별·정렬용 인덱스만 덧붙인다.
			 * valueIndex는 getRowId의 단일 키이며, orderIndex는 응답 순서를 1부터 표현한다.
			 */
			const rows = (response.data.data ?? []).map((row, index) => ({
				...defaultPRD1020_R01Dto,
				...row,
				valueIndex: index,
				orderIndex: index + 1,
				orderIndexSeq: 0,
			})) as PRD1020_R01Dto[];

			setPRD1020_R01List(rows);
			// 조회에 실제 사용한 조건을 별도 복사해 이후 화면 확장 시 현재 조건과 구분한다.
			setPRD1020_R01PastFilter(PRD1020_R01Filter);
			if (rows.length === 0) {
				showWarning("데이터가 없습니다.");
				return;
			}

			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${rows.length}건)`);
			setTimeout(() => setMessage(""), 5000);
		} catch (error) {
			console.error("PRD1020_R01 조회 실패", error);
			// 실패한 요청의 이전 결과가 화면에 남지 않도록 목록을 비운다.
			setPRD1020_R01List([]);
			showWarning("데이터 조회에 실패했습니다.");
		} finally {
			setLoadingSpinner(false);
		}
	}, [
		PRD1020_R01Filter,
		setLoadingSpinner,
		setMessage,
		setPRD1020_R01List,
		setPRD1020_R01PastFilter,
		setScrollTop,
	]);

	/** 현재 검색조건을 그대로 사용하여 서버 생성 XLSX를 지정 파일명으로 저장한다. */
	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			const response = await downloadExcel_PRD1020_R01(PRD1020_R01Filter);
			downloadSettingExcel({
				responseData: response.data.data,
				fileName: "생산계획 대비 실적조회-excel.xlsx",
			});
		} finally {
			setLoadingSpinner(false);
		}
	}, [PRD1020_R01Filter, setLoadingSpinner]);

	return { fetchData, handleExcelClick };
};
